import { useState, useEffect, useCallback, useMemo } from 'react';
import { Modal, Button, ButtonGroup, Form } from 'react-bootstrap';
import { useAppSelector } from '../../redux/store';
import api from '../../Services/api';
import Globals from '../../Globals.json';
import { queryClient } from '../../apiQuery/queryClient';
import { notify, notifyError } from '../notify/notify-component';
import * as S from '../ModalAjusteImpressoras/styled';
import {
  ActionRow,
  CategoryList,
  Step,
  StepNumber,
  StepTitle,
  Summary,
} from './styled';

interface Props {
  show: boolean;
  onHide: () => void;
}

interface FlatCategory {
  id: number;
  name: string;
  level: number;
  childrenIds: number[];
}

type FieldKey = 'is_active' | 'self_service';
type Action = 'keep' | 'on' | 'off';

const FIELDS: { key: FieldKey; label: string }[] = [
  { key: 'is_active', label: 'Ativo' },
  { key: 'self_service', label: 'Autoatendimento' },
];

const ACTIONS: { value: Action; label: string; variant: string }[] = [
  { value: 'keep', label: 'Não alterar', variant: 'secondary' },
  { value: 'on', label: 'Ativar', variant: 'success' },
  { value: 'off', label: 'Desativar', variant: 'danger' },
];

const initialActions = (): Record<FieldKey, Action> => ({
  is_active: 'keep',
  self_service: 'keep',
});

const normalize = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

// Achata a árvore e guarda os ids das descendentes de cada categoria.
function flatten(nodes: any[], level: number, list: FlatCategory[]): number[] {
  const ids: number[] = [];
  nodes.forEach((node) => {
    const entry: FlatCategory = {
      id: node.id,
      name: node.name,
      level,
      childrenIds: [],
    };
    list.push(entry);
    ids.push(node.id);
    entry.childrenIds = flatten(node.relationships?.children || [], level + 1, list);
    ids.push(...entry.childrenIds);
  });
  return ids;
}

export function ModalAtivarEmMassa({ show, onHide }: Props) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);

  const [categories, setCategories] = useState<FlatCategory[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [actions, setActions] = useState(initialActions);
  const [search, setSearch] = useState<string>('');
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);

  const loadData = useCallback(async () => {
    setLoadingData(true);
    try {
      const response = await api.get(`${Globals.api.treeCategories}`, {
        params: {
          page: 1,
          paginate: false,
          store_id: storeMe?.storeActive?.id,
        },
      });
      const list: FlatCategory[] = [];
      flatten(response.data.data || [], 0, list);
      setCategories(list);
    } catch (error) {
      notifyError('Erro ao carregar categorias');
      console.error('Erro ao carregar categorias:', error);
    } finally {
      setLoadingData(false);
    }
  }, [storeMe?.storeActive?.id]);

  useEffect(() => {
    if (show) {
      setSelected(new Set());
      setActions(initialActions());
      setSearch('');
      loadData();
    }
  }, [show, loadData]);

  // Só os campos com ação (Ativar/Desativar) entram no payload.
  const attributes = useMemo(
    () =>
      FIELDS.reduce((acc, { key }) => {
        if (actions[key] !== 'keep') acc[key] = actions[key] === 'on';
        return acc;
      }, {} as Partial<Record<FieldKey, boolean>>),
    [actions]
  );

  const hasAttributes = Object.keys(attributes).length > 0;
  const ready = hasAttributes && selected.size > 0;

  const summary = useMemo(() => {
    if (!hasAttributes) return 'Escolha o que deseja ativar ou desativar.';
    if (selected.size === 0) return 'Selecione as categorias que serão alteradas.';

    const parts: string[] = [];
    if (actions.is_active !== 'keep') {
      parts.push(actions.is_active === 'on' ? 'ativar' : 'desativar');
    }
    if (actions.self_service !== 'keep') {
      parts.push(
        `${actions.self_service === 'on' ? 'ligar' : 'desligar'} o autoatendimento`
      );
    }
    return (
      <>
        Vai <b>{parts.join(' e ')}</b> de <b>{selected.size} categoria(s)</b> e
        de todos os produtos delas.
      </>
    );
  }, [actions, hasAttributes, selected.size]);

  const visibleCategories = useMemo(() => {
    const term = normalize(search.trim());
    if (!term) return categories;
    return categories.filter((c) => normalize(c.name).includes(term));
  }, [categories, search]);

  // Marcar/desmarcar uma categoria aplica o mesmo estado às subcategorias.
  const handleToggle = (category: FlatCategory) => {
    const checked = selected.has(category.id);
    setSelected((prev) => {
      const next = new Set(prev);
      [category.id, ...category.childrenIds].forEach((id) =>
        checked ? next.delete(id) : next.add(id)
      );
      return next;
    });
  };

  const allVisibleChecked =
    visibleCategories.length > 0 &&
    visibleCategories.every((c) => selected.has(c.id));

  const handleToggleVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      visibleCategories.forEach((c) =>
        allVisibleChecked ? next.delete(c.id) : next.add(c.id)
      );
      return next;
    });

  const handleSave = async () => {
    if (!storeMe?.storeActive?.id) {
      notifyError('Loja não identificada');
      return;
    }

    setSaving(true);
    try {
      await api.post(Globals.api.bulkAttributesCategories, {
        store_id: storeMe.storeActive.id,
        category_ids: Array.from(selected),
        attributes,
        // O hook do model Category já propaga is_active/self_service aos produtos.
        apply_to_products: true,
        apply_to_subcategories: false,
        confirm: true,
      });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['category'] });
      notify(`${selected.size} categoria(s) atualizada(s) com sucesso!`);
      onHide();
    } catch (error) {
      notifyError('Erro ao atualizar categorias');
      console.error('Erro ao atualizar categorias:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <S.ModalWrapper show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Ativar em Massa por Categoria</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loadingData ? (
          <div className="text-center p-4">
            <div className="spinner-border" role="status" />
          </div>
        ) : (
          <>
            <Step>
              <StepTitle>
                <StepNumber>1</StepNumber> O que deseja fazer?
              </StepTitle>
              {FIELDS.map(({ key, label }) => (
                <ActionRow key={key}>
                  <strong>{label}</strong>
                  <ButtonGroup size="sm">
                    {ACTIONS.map((action) => (
                      <Button
                        key={action.value}
                        variant={
                          actions[key] === action.value
                            ? action.variant
                            : `outline-${action.variant}`
                        }
                        onClick={() =>
                          setActions((prev) => ({ ...prev, [key]: action.value }))
                        }
                      >
                        {action.label}
                      </Button>
                    ))}
                  </ButtonGroup>
                </ActionRow>
              ))}
            </Step>

            <Step>
              <StepTitle>
                <StepNumber>2</StepNumber> Em quais categorias?
                <S.Muted className="ms-auto fw-normal">
                  {selected.size} selecionada(s)
                </S.Muted>
              </StepTitle>

              <S.Toolbar>
                <Form.Control
                  size="sm"
                  placeholder="Buscar categoria..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={handleToggleVisible}
                  disabled={visibleCategories.length === 0}
                >
                  {allVisibleChecked ? 'Desmarcar todas' : 'Marcar todas'}
                </Button>
              </S.Toolbar>

              <CategoryList>
                {visibleCategories.length === 0 ? (
                  <S.Muted className="d-block p-3">
                    Nenhuma categoria encontrada.
                  </S.Muted>
                ) : (
                  visibleCategories.map((category) => {
                    const checked = selected.has(category.id);
                    return (
                      <S.Row
                        key={category.id}
                        $checked={checked}
                        $level={search ? 0 : category.level}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleToggle(category)}
                        />
                        {category.name.toUpperCase()}
                      </S.Row>
                    );
                  })
                )}
              </CategoryList>
            </Step>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Summary $ready={ready}>{summary}</Summary>
        <div className="d-flex gap-2">
          <Button variant="secondary" onClick={onHide} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving || loadingData || !ready}
          >
            {saving
              ? 'Aplicando...'
              : ready
              ? `Aplicar em ${selected.size} categoria(s)`
              : 'Aplicar'}
          </Button>
        </div>
      </Modal.Footer>
    </S.ModalWrapper>
  );
}
